import groupBy from 'lodash/groupBy';
import startCase from 'lodash/startCase';
import React from 'react';

import { GUID, PersonOrg } from '@ndvr/customer-api';
import { JournalEventType } from '@ndvr/ncrm-api';

import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { formatDateTime, parseLocalDate } from 'utils';
import { useJournalEvents } from 'views/admin/dev-queries';
import { AccountNameLink, DataGrid, Text, Title, VerticalLayout } from 'widgets';

export const JournalEvents = ({ personOrgGUID }: { personOrgGUID: GUID<PersonOrg> }) => {
    const { data } = useJournalEvents();
    const personEvents = data?.filter(({ loggedInPersonOrgGUID }) => personOrgGUID === loggedInPersonOrgGUID);
    const groupedByType = groupBy(personEvents, 'type');

    return (
        <VerticalLayout $gap={16}>
            <Card className="max-h-[300px] overflow-auto">
                <CardTitle className="px-4 pt-4">
                    <Title content>Event stats</Title>
                </CardTitle>
                <CardContent>
                    <DataGrid
                        values={(Object.keys(JOURNAL_EVENT_MAP) as JournalEventType[])
                            .filter((eventType: JournalEventType) => groupedByType[eventType] !== undefined)
                            .map((eventType: JournalEventType) => ({
                                label: JOURNAL_EVENT_MAP[eventType] || startCase(eventType.toLowerCase()),
                                value: groupedByType[eventType].length,
                            }))}
                    />
                </CardContent>
            </Card>

            <Card className="max-h-[300px] overflow-auto">
                <CardTitle className="px-4 pt-4">
                    <Title content>Event log</Title>
                </CardTitle>
                <CardContent>
                    <DataGrid
                        values={personEvents
                            ?.sort(
                                (a, b) => parseLocalDate(b.timestamp).getTime() - parseLocalDate(a.timestamp).getTime(),
                            )
                            ?.map((event) => ({
                                label: formatDateTime(event.timestamp, 'PRECISE'),
                                value:
                                    event.type === JournalEventType.CREATE_PORTFOLIO ||
                                    event.type === JournalEventType.RUN_OPTIMIZATION ? (
                                        <Text>
                                            {startCase(event.type.toLowerCase())}
                                            {' - '}
                                            <AccountNameLink accountGUID={event.portfolioGUID} />
                                        </Text>
                                    ) : (
                                        startCase(event.type.toLowerCase())
                                    ),
                            }))}
                    />
                </CardContent>
            </Card>
        </VerticalLayout>
    );
};

export const JOURNAL_EVENT_MAP: { [key in JournalEventType]?: string } = {
    [JournalEventType.CHANGE_PASSWORD]: 'Create/Change Password',
    [JournalEventType.CHANGE_PREFERENCES]: 'Change Draft Preferences',
    [JournalEventType.CHANGE_REQUIREMENTS]: 'Change Draft Requirements',
    [JournalEventType.CREATE_PORTFOLIO]: 'Create Draft Portfolio',
    [JournalEventType.FILE_DOWNLOAD]: 'Download File',
    [JournalEventType.FOLDER_ZIP_DOWNLOAD]: 'Download Folder Zip',
    [JournalEventType.RUN_OPTIMIZATION]: 'Run Optimization',
    [JournalEventType.SIGN_IN]: 'Sign In',
    [JournalEventType.SIGNUP_REQUEST]: 'Signup Request',
};
